import { useId, useState } from 'react';
import {
  ChartFrame,
  EmptyChart,
  INK,
  Legend,
  MotionStyles,
  XAxis,
  YAxis,
  seriesColour,
  signedText,
  tickColumnWidth,
  useMeasuredWidth,
  usePrefersReducedMotion,
  type ChartTable,
  type TooltipRow,
  type TooltipState,
} from './Chart';
import { areaPath, bandScale, linePath, linearScale, monthLabel, niceDomain, niceTicks, spansYears } from './layout';

/**
 * One or several lines over the months, with an optional wash under one of
 * them and the zero line always drawn.
 *
 * ── WHAT IT IS FOR ──
 * "Profit, running": the cumulative net profit across every book as the
 * heavy line with a wash beneath it, and each book's own running total as a
 * thin line in its colour, so a person can see which book is carrying the
 * others and when it started to. Zero is marked because a running profit
 * that dips below it is the fact the chart exists to show, and an axis
 * autoscaled to the data would put that crossing anywhere.
 *
 * ── THE WASH IS A WASH ──
 * The area under the primary line is the series hue at a tenth of its
 * strength: enough to read "above zero" at a glance, not enough to hide the
 * thin lines that cross it. It started at 16% and read as a grey slab on a
 * flat run of months - a filled area chart, which says "volume" - so it is
 * a tenth at the line and next to nothing at the baseline.
 *
 * ── ONE MONTH IS A DOT, NOT A LINE ──
 * The page opens on "this month", so the first thing most people see here
 * is a period of one month - and a line through one point is a point. Every
 * series is then drawn as a dot, the line the chart is about carries its
 * value beside it, and the caption under the chart says a longer period
 * shows the run. An empty frame with one dot in the middle read as a chart
 * that had failed to load.
 */

export interface LineSeries {
  id: string;
  name: string;
  /** A palette token, or null for the theme's primary. */
  colour: string | null;
  values: number[];
  /** 2px for the line the chart is about, 1.25 for the ones behind it. */
  width?: number;
  area?: boolean;
}

export interface LineChartProps {
  months: string[];
  lines: LineSeries[];
  currency: string;
  title: string;
  summary: string;
  empty: { title: string; description?: string };
  height?: number;
  /**
   * More for the readout of a month, after the lines - the prop `StackedBars`
   * already takes, so a page enriches either chart the same way.
   *
   * A running total is the chart that needs it most: the line says where the
   * year stands, and the question straight after is how much of that was this
   * month. The chart knows its points; the page knows what else is true.
   */
  extraRows?: (monthIndex: number) => TooltipRow[];
}

/** `left` is not here: the tick column is measured from its labels - see `tickColumnWidth`. */
const MARGIN = { top: 14, right: 16, bottom: 26 };

export function LineChart({ months, lines, currency, title, summary, empty, height = 240, extraRows }: LineChartProps) {
  const [frameRef, width] = useMeasuredWidth<HTMLDivElement>();
  const reduced = usePrefersReducedMotion();
  const gradientId = useId();
  const [active, setActive] = useState<number | null>(null);

  const moved = lines.some((line) => line.values.some((value) => value !== 0));
  if (months.length === 0 || !moved) return <EmptyChart title={empty.title} description={empty.description} />;

  let lo = 0;
  let hi = 0;
  for (const line of lines) {
    for (const value of line.values) {
      lo = Math.min(lo, value);
      hi = Math.max(hi, value);
    }
  }
  const domain = niceDomain(lo, hi);
  const ticks = niceTicks(lo, hi);
  const plotLeft = tickColumnWidth(ticks, currency);
  const plotRight = Math.max(plotLeft + 40, width - MARGIN.right);
  const plotTop = MARGIN.top;
  const plotBottom = height - MARGIN.bottom;
  const y = linearScale(domain, [plotBottom, plotTop]);
  // Points sit at the centre of each month's slot, so the first and last
  // months have room for their dot and label rather than being cut in half.
  const band = bandScale(months.length, plotLeft, plotRight, 0, 0.5);
  const centre = (i: number) => band.x(i) + band.band / 2;
  const withYear = spansYears(months);
  const single = months.length === 1;

  const pointsOf = (line: LineSeries) => months.map((_, i) => ({ x: centre(i), y: y(line.values[i] ?? 0) }));

  const tooltipFor = (i: number): TooltipState => ({
    x: centre(i),
    y: Math.min(...lines.map((line) => y(line.values[i] ?? 0))),
    title: monthLabel(months[i] ?? '', 'long'),
    rows: [
      ...lines.map((line) => ({
        key: line.id,
        colour: line.colour,
        kind: 'line' as const,
        label: line.name,
        value: signedText(line.values[i] ?? 0, currency),
      })),
      ...(extraRows ? extraRows(i) : []),
    ],
  });

  const step = (direction: -1 | 1 | 'first' | 'last') => {
    setActive((current) => {
      if (direction === 'first') return 0;
      if (direction === 'last') return months.length - 1;
      const next = (current ?? (direction === 1 ? -1 : months.length)) + direction;
      return Math.max(0, Math.min(months.length - 1, next));
    });
  };

  const pointerMonth = (event: React.PointerEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return band.index(event.clientX - rect.left);
  };

  const table: ChartTable = {
    caption: `${title}, in ${currency}`,
    columns: ['Month', ...lines.map((line) => line.name)],
    rows: months.map((month, i) => ({
      key: month,
      cells: [monthLabel(month, 'long'), ...lines.map((line) => signedText(line.values[i] ?? 0, currency))],
    })),
  };

  // Behind first, so the line the chart is about is painted last and on top.
  const ordered = [...lines].sort((a, b) => (a.width ?? 2) - (b.width ?? 2));
  const legend = lines.length > 1 ? lines.map((line) => ({ key: line.id, label: line.name, colour: line.colour, kind: 'line' as const })) : [];

  return (
    <div className="space-y-2">
      <ChartFrame title={title} table={table} tooltip={active === null ? null : tooltipFor(active)} onStep={step} onLeave={() => setActive(null)} frameRef={frameRef}>
        <svg
          role="img"
          aria-label={summary}
          width="100%"
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          className="block select-none"
          style={{ touchAction: 'pan-y' }}
          onPointerMove={(event) => {
            if (event.pointerType === 'touch') return;
            setActive(pointerMonth(event));
          }}
          onPointerDown={(event) => {
            if (event.pointerType !== 'touch') return;
            const month = pointerMonth(event);
            setActive((current) => (current === month ? null : month));
          }}
          onPointerLeave={(event) => {
            if (event.pointerType !== 'touch') setActive(null);
          }}
        >
          <defs>
            {ordered
              .filter((line) => line.area)
              .map((line) => (
                <linearGradient key={line.id} id={`${gradientId}${line.id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor={seriesColour(line.colour)} stopOpacity="0.1" />
                  <stop offset="1" stopColor={seriesColour(line.colour)} stopOpacity="0.02" />
                </linearGradient>
              ))}
            <MotionStyles enabled={!reduced} />
          </defs>

          <YAxis ticks={ticks} y={y} left={plotLeft} right={plotRight} currency={currency} />

          {active !== null ? (
            <line aria-hidden x1={centre(active)} x2={centre(active)} y1={plotTop} y2={plotBottom} stroke={INK.zero} strokeWidth={1} shapeRendering="crispEdges" />
          ) : null}

          {ordered.map((line) => {
            const points = pointsOf(line);
            const ink = seriesColour(line.colour);
            const stroke = line.width ?? 2;
            const first = points[0];
            return (
              <g key={line.id} style={reduced ? undefined : { animation: 'chart-fade 500ms ease-out both' }}>
                {line.area && !single ? <path aria-hidden d={areaPath(points, y(0))} fill={`url(#${gradientId}${line.id})`} /> : null}
                {!single ? (
                  <path
                    d={linePath(points)}
                    fill="none"
                    stroke={ink}
                    strokeWidth={stroke}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    opacity={stroke < 2 ? 0.85 : 1}
                    pathLength={1}
                    style={reduced ? undefined : { strokeDasharray: 1, animation: 'chart-draw 700ms ease-out both' }}
                  >
                    <title>{line.name}</title>
                  </path>
                ) : null}
                {points.map((point, i) =>
                  single || stroke >= 2 || active === i ? (
                    <circle
                      key={months[i]}
                      cx={point.x}
                      cy={point.y}
                      r={active === i ? 5 : stroke >= 2 ? 3.5 : single ? 4 : 3}
                      fill={ink}
                      stroke={INK.surface}
                      strokeWidth={2}
                    >
                      <title>{`${monthLabel(months[i] ?? '', 'long')} · ${line.name}: ${signedText(line.values[i] ?? 0, currency)}`}</title>
                    </circle>
                  ) : null,
                )}
                {/* One month: the line the chart is about says its figure
                    beside its dot, since there is no run to read it from. */}
                {single && stroke >= 2 && first ? (
                  <text aria-hidden x={first.x + 10} y={first.y} dy="0.35em" fontSize={12} fontWeight={500} fill={INK.text} className="tabular-nums">
                    {signedText(line.values[0] ?? 0, currency)}
                  </text>
                ) : null}
              </g>
            );
          })}

          <XAxis labels={months.map((month) => monthLabel(month, 'short', withYear))} centre={centre} y={height - 8} band={band.step} />
        </svg>
      </ChartFrame>
      <Legend items={legend} />
    </div>
  );
}
